import { AnyAction } from 'redux';
import { Action, Result, ApiAction, AsyncResult } from '../index';
import api from '../redux-api';
import { mapApiRecentSearchToRecentSearchItems, mapApiResultsToQuickSearchItems } from '../../utils/quick-search';
import { QUICK_SEARCH_FILTERS } from '@/enums/QuickSearch';

// Types
import { QuickSearchState } from './types/quick-search';

export enum QUICK_SEARCH_TYPE {
  FUNDS = 'FUNDS',
  PORTFOLIOS = 'PORTFOLIOS',
  STOCKS = 'STOCKS',
  BENCHMARKS = 'BENCHMARKS',
  BENCHMARK_PROXIES = 'BENCHMARK_PROXIES',
  BONDS = 'BONDS',
}

export enum QUICK_SEARCH_REDUCER_NAME {
  COMPARISON = 'COMPARISON',
  EDIT_PORTFOLIO = 'EDIT_PORTFOLIO',
  EDIT_BENCHMARK = 'EDIT_BENCHMARK',
  EDIT_BMO_PORTFOLIO = 'EDIT_BMO_PORTFOLIO',
}

// Initial State
const INITIAL_STATE: QuickSearchState = {
  recentSearchItems: [],
  results: [],
};

// Actions
const QUICK_SEARCH_ITEMS: ApiAction = {
  type: 'connect/quick-search',
  endpoint: 'quick-search/',
};

const LOAD_RECENT_SEARCH_ITEMS: ApiAction = {
  type: 'connect/quick-search/load-recent-search-items',
  endpoint: 'recent-search/',
};

const CLEAR_RESULTS: Action = {
  type: 'connect/quick-search/clear',
};

const SEARCH_BMO_FUNDS: ApiAction = {
  type: 'connect/bmo-gif/search-bmo-funds',
  endpoint: 'quick-search/bmo-gif',
};

const SET_FOCUSED_QUICK_SEARCH: Action = {
  type: 'connect/quick-search/set-focused-quick-search',
};

// Reducer
export default function reducer(state: QuickSearchState = INITIAL_STATE, action: AnyAction): QuickSearchState {
  switch (action.type) {
    case QUICK_SEARCH_ITEMS.type: {
      return {
        ...state,
        currentRequest: action.request,
      };
    }
    case `${QUICK_SEARCH_ITEMS.type}_SUCCESS`: {
      return {
        ...state,
        results: mapApiResultsToQuickSearchItems(action.data.data),
        currentRequest: null,
      };
    }
    case `${QUICK_SEARCH_ITEMS.type}_ERROR`: {
      return {
        ...state,
        results: [],
        currentRequest: null,
      };
    }
    case CLEAR_RESULTS.type: {
      return {
        ...state,
        results: [],
      };
    }
    case LOAD_RECENT_SEARCH_ITEMS.type: {
      return {
        ...state,
        recentSearchItems: [],
        currentRequest: action.request,
      };
    }
    case `${LOAD_RECENT_SEARCH_ITEMS.type}_SUCCESS`: {
      return {
        ...state,
        recentSearchItems: mapApiRecentSearchToRecentSearchItems(action.data),
        currentRequest: null,
      };
    }
    case `${LOAD_RECENT_SEARCH_ITEMS.type}_ERROR`: {
      return {
        ...state,
        recentSearchItems: [],
        currentRequest: null,
      };
    }
    case `${SEARCH_BMO_FUNDS.type}_SUCCESS`: {
      return {
        ...state,
        results: mapApiResultsToQuickSearchItems(action.data.data),
      };
    }
    case `${SEARCH_BMO_FUNDS.type}_ERROR`: {
      return {
        ...state,
        results: [],
      };
    }
    case SET_FOCUSED_QUICK_SEARCH.type: {
      return {
        ...state,
        focused: action.focused,
        enableOnlyProxies: action.enableOnlyProxies,
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const fetchQuickSearchResults =
  (
    query: string,
    reducerName: string,
    types?: QUICK_SEARCH_TYPE[] | QUICK_SEARCH_FILTERS[],
    excludeSubtypes: boolean = false,
    limit: number = 25,
    universe?: string,
  ): AsyncResult<void> =>
  (dispatch, getState) => {
    const {
      intl: { locale },
      quickSearch: { currentRequest },
    } = getState();

    const options: any = {
      query,
      locale,
      limit,
      portfolioLimit: 4,
      excludeSubtypes,
    };

    if (types) {
      options.types = types.map(t => t.toUpperCase());
    }

    if (universe) {
      options.universe = universe.toUpperCase();
    }

    if (currentRequest) {
      currentRequest.cancel();
    }

    return api.get({ ...QUICK_SEARCH_ITEMS, name: reducerName }, options);
  };

export const clearResults =
  (reducerName: string): Result<void> =>
  dispatch => {
    dispatch({ ...CLEAR_RESULTS, name: reducerName });
  };

export const fetchBMOQuickSearchResults =
  (query: string, reducerName: string, classType: string, planOption: string): AsyncResult<void> =>
  (dispatch, getState) => {
    const {
      intl: { locale },
    } = getState();
    const params = {
      locale,
      query,
      fundClass: classType,
      planOption,
    };

    return api.get({ ...SEARCH_BMO_FUNDS, name: reducerName }, params);
  };

export const loadRecentSearchItems =
  (reducerName: string): AsyncResult<void> =>
  (dispatch, getState) => {
    const {
      quickSearch: { currentRequest },
    } = getState();

    if (currentRequest) {
      currentRequest.cancel();
    }

    return api.get({ ...LOAD_RECENT_SEARCH_ITEMS, name: reducerName });
  };

const setFocusedQuickSearch =
  (reducerName: string) =>
  (focused?: boolean, enableOnlyProxies?: boolean): Result<void> =>
  dispatch => {
    dispatch({
      ...SET_FOCUSED_QUICK_SEARCH,
      name: reducerName,
      focused,
      enableOnlyProxies,
    });
  };

export const setFocusedQuickSearchComparison = setFocusedQuickSearch(QUICK_SEARCH_REDUCER_NAME.COMPARISON);
export const setFocusedQuickSearchEditPorfolio = setFocusedQuickSearch(QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO);
